import { DEFAULT_CANVAS_NAME, useCanvasById } from "@/api/canvas";
import { useListRelationshipsWithResources } from "@/api/relations";
import { CanvasLinkAccessSection } from "@/components/ShareDialog/CanvasLinkAccessSection";
import { ShareDialog } from "@/components/ShareDialog/ShareDialog";
import { PERMISSIONS } from "@/constants/permissions";
import { useHasPermission } from "@/hooks/usePermissions";
import type { SubjectObjectType } from "@cohesive/api-client";
import { UserAvatarList } from "@cohesive/ui-components";
import { useOthersMapped } from "@liveblocks/react";
import { Users } from "lucide-react";
import { motion } from "motion/react";
import { useMemo } from "react";
import { useCanvasChromeEnterAnimation } from "../hooks/useCanvasChromeEnterAnimation";

interface CanvasShareButtonProps {
  canvasId: string;
}

export function CanvasShareButton({ canvasId }: CanvasShareButtonProps) {
  const { data: canvas } = useCanvasById(canvasId);
  // Subscribe to the present user ids only — not the full others list, which
  // changes on every remote cursor packet and would re-render this button
  // (and, if hoisted, the whole page) per packet.
  const presentUsers = useOthersMapped((other) => other.id);
  const presentUserIds = useMemo(
    () => presentUsers.map(([, id]) => id),
    [presentUsers],
  );
  const canvasName = canvas?.name || DEFAULT_CANVAS_NAME;
  const isOrgAdmin = useHasPermission(PERMISSIONS.ORG_ADMIN.ALL);

  const { data: allRelationships = [] } = useListRelationshipsWithResources(
    "canvas",
    canvasId,
  );

  // The canvas#workspace relation is backend-only plumbing for workspace-scoped
  // access. Hide it from the avatar list so it doesn't show up as a ghost member.
  const relationships = allRelationships.filter(
    (r) => (r.subject_object_type as string) !== "workspace",
  );

  const shareConfig = {
    resourceObjectType: "canvas" as const,
    resourceObjectId: canvasId,
    subjectObjectType: [
      ...(isOrgAdmin ? ["organization" as SubjectObjectType] : []),
      "organization_user" as SubjectObjectType,
      ...(isOrgAdmin ? ["group" as SubjectObjectType] : []),
    ],
    title: `Share "${canvasName}"`,
  };

  const shouldAnimateChromeEnter = useCanvasChromeEnterAnimation();

  return (
    <motion.div
      initial={shouldAnimateChromeEnter ? { opacity: 0, y: -12 } : false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ opacity: { duration: 0.2 }, y: { duration: 0.2 } }}
      className="flex items-center"
      data-canvas-control
    >
      <ShareDialog
        resourceObjectType={shareConfig.resourceObjectType}
        resourceObjectId={shareConfig.resourceObjectId}
        subjectObjectType={shareConfig.subjectObjectType}
        title={shareConfig.title}
        linkAccess={
          canvas ? <CanvasLinkAccessSection canvas={canvas} /> : undefined
        }
      >
        <button
          type="button"
          aria-label={`Share "${canvasName}"`}
          className="flex h-10 items-center rounded-full cursor-pointer focus:outline-none focus-visible:outline-[1.5px] focus-visible:outline-rust focus-visible:outline-offset-0.5"
        >
          {relationships.length > 0 ? (
            <UserAvatarList
              relationships={relationships}
              maxVisible={4}
              presentUserIds={presentUserIds}
            />
          ) : (
            <span className="flex size-8 items-center justify-center rounded-full border-2 border-background bg-muted text-muted-foreground">
              <Users className="size-4" strokeWidth={2} />
            </span>
          )}
        </button>
      </ShareDialog>
    </motion.div>
  );
}
